import { useEffect, useState, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Input,
  Typography,
  Flex,
  Spin,
  Tabs,
  ConfigProvider,
} from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as Search } from '../../../assets/icons/search.svg';
import { ReactComponent as SearchBlack } from '../../../assets/icons/search-black.svg';
import {
  DARK,
  HYPHEN,
  ORGANISATION_KEYS,
  RESTRICT_DATA_DATE,
  VEHICLE_STATUS,
} from '../../../utilities/constants';
import { useTheme } from '../../../context/ThemeContext';
import './TripsList.css';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import CustomRangePicker from './../../../components/CustomRangePicker/CustomRangePicker';
import dayjs from 'dayjs';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';
import {
  getFormattedDateFromTimestamp,
  sanitizeInput,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import useApiPolling from '../../../hooks/useApiPolling';
import TripsListCard from './TripsListCard';
import { UserRolesAndPermissionsContext } from '../../../context/UserRolesAndPermissionsContext';

const { Title, Text } = Typography;

const formatTripResults = (results, pageNumber) =>
  (results || []).map((trip, index) => ({
    key: `${pageNumber}-${index}`,
    trip_id: trip?.notification?.trip_id || HYPHEN,
    registration: trip?.notification?.registration || HYPHEN,
    vendor: trip?.notification?.vendor?.vendorName || HYPHEN,
    ridebuddy_deviceid: trip?.notification?.ridebuddy_deviceid || HYPHEN,
    driver_id: trip?.notification?.driver_id || HYPHEN,
    sosCount: trip?.sos_count || 0,
    criticalCount: trip?.critical_count || 0,
    majorCount: trip?.major_anomaly_count || 0,
    minorCount: trip?.minor_anomaly_count || 0,
    trip_date:
      getFormattedDateFromTimestamp(trip?.notification?.trip_start_time) || '',
    driver_name: trip?.notification?.drivername || HYPHEN,
    sticker_id: trip?.notification?.sticker_id || null,
  }));

const TripsList = ({
  selectedTripDetails,
  setSelectedTripDetails,
  activeTabKey,
  setActiveTabKey,
  pageActiveKey,
  setIsTripListLoading,
}) => {
  const { theme } = useTheme();
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const currentDate = dayjs().format('DD-MM-YYYY');
  const previousDate = dayjs().subtract(1, 'day').format('DD-MM-YYYY');
  const [tripData, setTripData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(['', '']);
  const scrollableRef = useRef(null);
  const isFirstCall = useRef(true);
  const isFetching = useRef(false);
  const { userDetails } = useContext(UserRolesAndPermissionsContext);

  const tabList = Object.entries(VEHICLE_STATUS).map(([key, tab]) => ({
    key: tab,
    label: key === VEHICLE_STATUS.Inactive ? 'Completed' : tab,
  }));

  const shouldSkipFetch = (startDate, endDate) =>
    (startDate === '' && endDate === '') || loading || isFetching.current;

  useEffect(() => {
    setIsTripListLoading?.(loading);
  }, [loading, setIsTripListLoading]);

  const buildTripReportPayload = (startDate, endDate) => ({
    filter: {
      trip_status: pageActiveKey === 'monitoring' ? activeTabKey : '',
      'tripId/vehicleNo': searchText,
      start_date: startDate,
      end_date: endDate,
    },
  });

  const updateSelectedTripIfNeeded = data => {
    if (!isFirstCall.current) {
      return;
    }
    setSelectedTripDetails(data?.[0]);
  };

  const updateTripData = (pageNumber, formattedData) => {
    if (pageNumber === 1) {
      setTripData(formattedData);
      updateSelectedTripIfNeeded(formattedData);
      return;
    }
    setTripData([...tripData, ...formattedData]);
  };

  const handleFetchError = (error, isPolling, pageNum) => {
    if (isFirstCall.current) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch trip details! Please try again after sometime.'
        ),
        `${error?.response?.data?.message ? 'info' : 'error'}`
      );
    }
    if (!isPolling && pageNum === 1) {
      setTripData([]);
      setSelectedTripDetails({});
      setPage(null);
    }
  };

  const fetchData = async (
    page,
    start_date = dateRange[0],
    end_date = dateRange[1],
    shouldShowLoading = true,
    isPolling = false
  ) => {
    if (shouldSkipFetch(start_date, end_date)) return;
    try {
      isFetching.current = true;
      setLoading(shouldShowLoading);
      const payload = buildTripReportPayload(start_date, end_date);
      const { data, status } = await API.getTripReports(
        page,
        20,
        payload,
        pageActiveKey !== 'monitoring'
      );
      if (status !== 200) return;
      const formattedData = formatTripResults(data?.results, page);
      updateTripData(page, formattedData);
      setPage(data?.next_page_number);
    } catch (error) {
      handleFetchError(error, isPolling, page);
    } finally {
      setLoading(false);
      isFetching.current = false;
      isFirstCall.current = false;
    }
  };

  const resetTripData = () => {
    setTripData([]);
    setSelectedTripDetails({});
    setPage(null);
    isFirstCall.current = true;
  };

  useEffect(() => {
    if (pageActiveKey !== 'monitoring') return;
    resetTripData();
    if (activeTabKey === VEHICLE_STATUS.Inactive) {
      fetchData(1, currentDate, currentDate);
    }
  }, [activeTabKey, pageActiveKey]);

  useEffect(() => {
    if (pageActiveKey === 'logs') {
      setDateRange([
        userDetails?.org === ORGANISATION_KEYS.JPMC
          ? RESTRICT_DATA_DATE.JPMC
          : '',
        previousDate,
      ]);
    }
  }, []);

  useEffect(() => {
    if (pageActiveKey === 'monitoring') return;
    if (dateRange[1] !== '') {
      resetTripData();
      fetchData(1);
    }
  }, [dateRange]);

  useApiPolling(
    () => fetchData(1, currentDate, currentDate, false, true),
    activeTabKey === VEHICLE_STATUS.Active && pageActiveKey === 'monitoring',
    [activeTabKey, pageActiveKey]
  );

  const handleSearch = () => {
    resetTripData();
    if (pageActiveKey === 'monitoring') {
      fetchData(1, currentDate, currentDate);
    } else if (pageActiveKey === 'logs') {
      fetchData(1);
    }
  };

  const getLogsStartDate = () =>
    userDetails?.org === ORGANISATION_KEYS.JPMC ? RESTRICT_DATA_DATE.JPMC : '';

  const handleScrollCheck = useCallback(() => {
    const element = scrollableRef.current;
    if (!element) return;
    const { scrollTop, scrollHeight, clientHeight } = element;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight * 0.9;
    if (!isAtBottom || loading || page === null) return;
    if (pageActiveKey === 'monitoring') {
      fetchData(page, currentDate, currentDate);
    } else if (pageActiveKey === 'logs') {
      fetchData(page, getLogsStartDate(), previousDate);
    }
  }, [page, loading]);

  useEffect(() => {
    const element = scrollableRef.current;

    if (element) {
      element.addEventListener('scroll', handleScrollCheck);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScrollCheck);
      }
    };
  }, [handleScrollCheck]);

  const onSearchTextChange = e => {
    setSearchText(sanitizeInput(e.target.value));
  };

  const handleTripClick = clickedItem => {
    setSelectedTripDetails(clickedItem);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            boxShadowTertiary: 'none',
          },
        },
      }}
    >
      <Card
        variant="borderless"
        className={`tripslist-card tripslist-card-${pageActiveKey}`}
      >
        {loading && <Spin size="large" className="loading" />}
        {pageActiveKey === 'logs' && (
          <Title level={4} style={{ marginTop: '0px', paddingBottom: '24px' }}>
            Past Trips
          </Title>
        )}
        <Flex gap={16} vertical>
          {pageActiveKey === 'monitoring' && (
            <Tabs
              type="card"
              size="large"
              className="border-tabs"
              tabBarGutter="2rem"
              tabBarStyle={{
                backgroundColor: 'transparent',
                padding: 0,
              }}
              defaultActiveKey={VEHICLE_STATUS.Active}
              activeKey={activeTabKey}
              items={tabList}
              onChange={activeKey => setActiveTabKey(activeKey)}
            />
          )}
          {pageActiveKey === 'logs' && (
            <CustomRangePicker setDateRange={setDateRange} maxDateCount={1} />
          )}
          <Input
            onChange={onSearchTextChange}
            onPressEnter={() => handleSearch()}
            size="large"
            placeholder="Trip ID/ Vehicle No."
            suffix={
              <Icon
                component={theme === DARK ? Search : SearchBlack}
                width={20}
                height={20}
                onClick={() => handleSearch()}
              />
            }
            className="input-search tripslist-search"
            disabled={loading}
          />
        </Flex>
        {tripData?.length === 0 ? (
          <EmptyCard description={<Text>No Trips Found</Text>} />
        ) : (
          <div
            style={{ overflowY: 'auto', height: '100%' }}
            ref={scrollableRef}
          >
            {tripData?.map((item, index) => (
              <TripsListCard
                item={item}
                key={`card-trip-detail-${item?.key}-${index}`}
                handleTripClick={handleTripClick}
                isSelected={item.key === selectedTripDetails?.key}
                backgroundColor={
                  item.key === selectedTripDetails?.key
                    ? 'var(--selected-bg-color, #D0D4D8)'
                    : 'var(--secondary-fill-color, #2E3033)'
                }
                tripStatus={
                  pageActiveKey === 'monitoring'
                    ? activeTabKey
                    : VEHICLE_STATUS.Inactive
                }
                iconBackgroundColor={
                  item.key === selectedTripDetails?.key
                    ? 'var(--primary-text-color, #FFFFFF)'
                    : 'var(--secondary-fill-color, #2E3033)'
                }
              />
            ))}
          </div>
        )}
      </Card>
    </ConfigProvider>
  );
};

TripsList.propTypes = {
  selectedTripDetails: PropTypes.object,
  setSelectedTripDetails: PropTypes.func.isRequired,
  activeTabKey: PropTypes.string.isRequired,
  setActiveTabKey: PropTypes.func.isRequired,
  pageActiveKey: PropTypes.string.isRequired,
};

export default TripsList;
